import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../../firebase/config';
import { doc, getDoc, collection, onSnapshot, addDoc, serverTimestamp, query, where, orderBy, updateDoc } from 'firebase/firestore';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { sendNotificationEmail, newLeaveRequestTemplate } from '../../firebase/notifications';
import MyLeaveRequests from './MyLeaveRequests';
import LeaveApprovalCenter from './LeaveApprovalCenter';
import LeaveStatements from './LeaveStatements'; // --- UPDATED: Import the LeaveStatements component ---

// Main router for the Leave section
export default function LeaveModule({ userProfile, showModal, appId }) {
    // --- UPDATED: Changed default view to 'myRequests' for a better user experience ---
    const [leaveView, setLeaveView] = useState('myRequests');

    let currentViewComponent;
    switch(leaveView) {
        case 'myRequests':
            currentViewComponent = <MyLeaveRequests userProfile={userProfile} appId={appId} />;
            break;
        case 'approvalCenter':
            currentViewComponent = <LeaveApprovalCenter userProfile={userProfile} appId={appId} />;
            break;
        // --- UPDATED: Added a case to render the LeaveStatements component ---
        case 'leaveStatements':
            currentViewComponent = <LeaveStatements userProfile={userProfile} appId={appId} />;
            break;
        case 'newRequest':
        default:
            currentViewComponent = <NewLeaveRequest userProfile={userProfile} showModal={showModal} appId={appId} onSuccessfulSubmit={() => setLeaveView('myRequests')} />;
            break;
    }

    return (
        <div>
            <LeaveNav setLeaveView={setLeaveView} activeView={leaveView} />
            <div className="mt-6">
                {currentViewComponent}
            </div>
        </div>
    );
}

// Sub-component for navigation within the Leave Module
function LeaveNav({ setLeaveView, activeView }) {
    const NavButton = ({ viewName, children }) => {
        const isActive = activeView === viewName;
        return (
            <button
                onClick={() => setLeaveView(viewName)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition ${
                    isActive ? 'bg-[#213368] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                }`}
            >
                {children}
            </button>
        );
    };

    return (
        <div className="bg-white p-2 rounded-lg shadow-sm border flex items-center space-x-2">
            <NavButton viewName="myRequests">My Leave Requests</NavButton>
            <NavButton viewName="newRequest">New Leave Request</NavButton>
            <NavButton viewName="approvalCenter">Leave Approval Center</NavButton>
            {/* --- UPDATED: Added the new navigation button for Leave Statements --- */}
            <NavButton viewName="leaveStatements">Leave Statements</NavButton>
        </div>
    );
}

// Confirmation Modal for Policy Violations
function ViolationConfirmModal({ violations, onCancel, onConfirm }) {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
                <h2 className="text-xl font-bold text-yellow-600 mb-4">Policy Violation Warning</h2>
                <p className="text-gray-600 mb-4">Your request violates the following company policies:</p>
                <ul className="list-disc list-inside bg-yellow-50 p-4 rounded-md text-yellow-800">
                    {violations.map((v, index) => <li key={index}>{v}</li>)}
                </ul>
                <p className="text-gray-600 mt-4">Your manager will be notified of these violations. Do you still wish to submit this request for their consideration?</p>
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onCancel} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={onConfirm} className="px-6 py-2 bg-yellow-500 text-white font-medium rounded-lg hover:bg-yellow-600">Submit Anyway</button>
                </div>
            </div>
        </div>
    );
}

// Helper function to determine the financial year string for a given date.
const getFinancialYear = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    // The financial year starts in April (month index 3)
    return month >= 3 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
};

// Component for creating a new leave request
function NewLeaveRequest({ userProfile, showModal, appId, onSuccessfulSubmit }) {
    const [leaveType, setLeaveType] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reason, setReason] = useState('');
    const [attachment, setAttachment] = useState(null);
    const [holidays, setHolidays] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [manager, setManager] = useState(null);
    const [policyViolations, setPolicyViolations] = useState([]);
    const [isViolationModalOpen, setIsViolationModalOpen] = useState(false);
    const [approvedLeaves, setApprovedLeaves] = useState([]);

    useEffect(() => {
        const fetchInitialData = async () => {
            if (userProfile.managerId) {
                const managerRef = doc(db, `/artifacts/${appId}/users/${userProfile.managerId}`);
                const managerSnap = await getDoc(managerRef);
                if (managerSnap.exists()) setManager(managerSnap.data());
            }

            const holidaysRef = collection(db, `/artifacts/${appId}/public/data/holidays`);
            onSnapshot(holidaysRef, (snapshot) => {
                setHolidays(snapshot.docs.map(d => d.data().date.toDate().toISOString().split('T')[0]));
            });

            const requestsQuery = query(
                collection(db, `/artifacts/${appId}/public/data/leaveRequests`),
                where("requesterId", "==", userProfile.uid),
                where("status", "==", "Approved")
            );
            onSnapshot(requestsQuery, (snapshot) => {
                setApprovedLeaves(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
                setIsLoading(false);
            });
        };
        fetchInitialData();
    }, [appId, userProfile.uid, userProfile.managerId]);

    const currentYearBalances = useMemo(() => {
        if (!userProfile?.dateOfJoining) return { vacation: 0, casual: 0, sick: 0 };

        const dateOfJoining = userProfile.dateOfJoining.toDate();
        const firstYearStr = getFinancialYear(dateOfJoining);
        const currentYearStr = getFinancialYear(new Date());

        let openingBalances = { vacation: 0, casual: 0, sick: 0 };

        const calculateProratedMonths = (joiningDate, fyStr) => {
            const fyEndYear = parseInt(fyStr.split('-')[1]);
            const fyEndDate = new Date(fyEndYear, 2, 31);

            let monthsInFY = (fyEndDate.getFullYear() - joiningDate.getFullYear()) * 12;
            monthsInFY -= joiningDate.getMonth();
            monthsInFY += fyEndDate.getMonth();
            monthsInFY += 1;

            if (joiningDate.getDate() > 1) {
                monthsInFY -= 1;
            }
            return Math.max(0, monthsInFY);
        };

        let yearIterator = parseInt(firstYearStr.split('-')[0]);
        const endYear = parseInt(currentYearStr.split('-')[0]) - 1;

        while(yearIterator <= endYear) {
            const yearStr = `${yearIterator}-${yearIterator + 1}`;
            const financialYearStart = new Date(yearIterator, 3, 1);
            let credits = { vacation: 18, casual: 6, sick: 6 };
            if (getFinancialYear(dateOfJoining) === yearStr) {
                const months = calculateProratedMonths(dateOfJoining, yearStr);
                credits = { vacation: 1.5 * months, casual: 0.5 * months, sick: 0.5 * months };
            }
            if (((financialYearStart - dateOfJoining) / (1000 * 60 * 60 * 24 * 365.25)) >= 5) {
                credits.vacation += 5;
            }
            const taken = approvedLeaves.filter(r => getFinancialYear(r.startDate.toDate()) === yearStr)
                .reduce((acc, req) => {
                    const type = req.leaveType.split(' ')[0].toLowerCase();
                    if (acc[type] !== undefined) acc[type] += req.duration;
                    return acc;
                }, { vacation: 0, casual: 0, sick: 0 });

            openingBalances.vacation = Math.min(openingBalances.vacation + credits.vacation - taken.vacation, 45);
            yearIterator++;
        }
        
        let currentCredits = { vacation: 18, casual: 6, sick: 6 };
        if (getFinancialYear(dateOfJoining) === currentYearStr) {
            const months = calculateProratedMonths(dateOfJoining, currentYearStr);
            currentCredits = { vacation: 1.5 * months, casual: 0.5 * months, sick: 0.5 * months };
        }
        const currentFyStart = new Date(parseInt(currentYearStr.split('-')[0]), 3, 1);
        if (((currentFyStart - dateOfJoining) / (1000 * 60 * 60 * 24 * 365.25)) >= 5) {
            currentCredits.vacation += 5;
        }

        const currentYearTaken = approvedLeaves.filter(r => getFinancialYear(r.startDate.toDate()) === currentYearStr)
            .reduce((acc, req) => {
                const type = req.leaveType.split(' ')[0].toLowerCase();
                if (acc[type] !== undefined) acc[type] += req.duration;
                return acc;
            }, { vacation: 0, casual: 0, sick: 0 });

        return {
            vacation: openingBalances.vacation + currentCredits.vacation - currentYearTaken.vacation,
            casual: currentCredits.casual - currentYearTaken.casual,
            sick: currentCredits.sick - currentYearTaken.sick,
        };

    }, [approvedLeaves, userProfile]);

    const calculatedDuration = useMemo(() => {
        if (!startDate || !endDate) return 0;
        let count = 0;
        const currentDate = new Date(startDate);
        const lastDate = new Date(endDate);
        while (currentDate <= lastDate) {
            const dayOfWeek = currentDate.getDay();
            const isWeekend = (dayOfWeek === 6) || (dayOfWeek === 0);
            const isHoliday = holidays.includes(currentDate.toISOString().split('T')[0]);
            if (!isWeekend && !isHoliday) count++;
            currentDate.setDate(currentDate.getDate() + 1);
        }
        return count;
    }, [startDate, endDate, holidays]);

    const proceedWithSubmission = async () => {
        setIsSubmitting(true);
        try {
            const leaveRequestData = {
                requesterId: userProfile.uid,
                requesterName: userProfile.name,
                department: userProfile.department,
                leaveType,
                startDate: new Date(startDate),
                endDate: new Date(endDate),
                duration: calculatedDuration,
                reason,
                status: 'Pending Manager Approval',
                createdAt: serverTimestamp(),
                hasAttachment: !!attachment,
                policyViolations: policyViolations,
            };
            await addDoc(collection(db, `/artifacts/${appId}/public/data/leaveRequests`), leaveRequestData);
            
            if (manager && manager.email) {
                const emailData = {
                    ...leaveRequestData,
                    startDate: { toDate: () => leaveRequestData.startDate },
                    endDate: { toDate: () => leaveRequestData.endDate }
                };
                const emailContent = newLeaveRequestTemplate(emailData, userProfile, policyViolations);
                await sendNotificationEmail({ to: [manager.email], ...emailContent, appId });
            }
            
            showModal("Success", "Your leave request has been submitted successfully.");
            onSuccessfulSubmit();
        } catch (error) {
            console.error("Error submitting leave request:", error);
            showModal("Error", "There was an error submitting your request. Please try again.");
        } finally {
            setIsSubmitting(false);
            setIsViolationModalOpen(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (startDate && endDate && getFinancialYear(new Date(startDate)) !== getFinancialYear(new Date(endDate))) {
            showModal("Cross-Year Request", "Your leave request spans across two financial years. Please submit two separate requests.");
            return;
        }

        const violations = [];
        if (calculatedDuration <= 0) {
            showModal("Invalid Dates", "The end date must be on or after the start date.");
            return;
        }

        const balanceKey = leaveType.split(' ')[0].toLowerCase();
        if (currentYearBalances[balanceKey] < calculatedDuration) {
            violations.push(`Insufficient leave balance for ${leaveType}.`);
        }

        if (leaveType === 'Vacation Leave' && calculatedDuration < 3) {
            violations.push("Vacation Leave must be taken for a minimum of three days.");
        }
        if (leaveType === 'Casual Leave' && calculatedDuration > 2) {
            violations.push("Casual Leave cannot be taken for more than 2 days.");
        }
        setPolicyViolations(violations);
        if (violations.length > 0) setIsViolationModalOpen(true);
        else await proceedWithSubmission();
    };
    
    if (isLoading) return <LoadingSpinner />;

    return (
        <>
            {isViolationModalOpen && (
                <ViolationConfirmModal violations={policyViolations} onCancel={() => setIsViolationModalOpen(false)} onConfirm={proceedWithSubmission} />
            )}
            <div className="bg-white p-8 rounded-lg shadow-md max-w-4xl mx-auto">
                <h2 className="text-2xl font-semibold text-[#213368] mb-6">New Leave Request</h2>
                
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Your Current Leave Balance</h3>
                    <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-md border">
                        <div className="text-center">
                            <p className="text-2xl font-bold text-green-600">{currentYearBalances.vacation.toFixed(1)}</p>
                            <p className="text-sm text-gray-500">Vacation Days</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-blue-600">{currentYearBalances.casual.toFixed(1)}</p>
                            <p className="text-sm text-gray-500">Casual Days</p>
                        </div>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-red-600">{currentYearBalances.sick.toFixed(1)}</p>
                            <p className="text-sm text-gray-500">Sick Days</p>
                        </div>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                     <div>
                        <label className="block text-sm font-medium text-gray-700">Approving Manager</label>
                        <div className="mt-1 p-2 border rounded-md bg-gray-100 text-gray-600">
                            {manager ? manager.name : 'No manager assigned'}
                        </div>
                    </div>

                    <div>
                        <label htmlFor="leaveType" className="block text-sm font-medium text-gray-700">Leave Type</label>
                        <select id="leaveType" value={leaveType} onChange={e => setLeaveType(e.target.value)} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm">
                            <option value="" disabled>Select a leave type...</option>
                            <option value="Vacation Leave">Vacation Leave</option>
                            <option value="Casual Leave">Casual Leave</option>
                            <option value="Sick Leave">Sick Leave</option>
                            <option value="Bereavement Leave">Bereavement Leave</option>
                            <option value="Maternity Leave">Maternity Leave</option>
                            <option value="Paternity Leave">Paternity Leave</option>
                        </select>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">Start Date</label>
                            <input type="date" id="startDate" value={startDate} onChange={e => setStartDate(e.target.value)} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"/>
                        </div>
                        <div>
                            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">End Date</label>
                            <input type="date" id="endDate" value={endDate} onChange={e => setEndDate(e.target.value)} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"/>
                        </div>
                    </div>

                    <div>
                        <p className="text-sm text-gray-600">Calculated Duration: <span className="font-bold">{calculatedDuration} working day(s)</span></p>
                    </div>

                    <div>
                        <label htmlFor="reason" className="block text-sm font-medium text-gray-700">Reason / Comments</label>
                        <textarea id="reason" value={reason} onChange={e => setReason(e.target.value)} rows="4" required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"></textarea>
                    </div>

                    {(leaveType === 'Bereavement Leave' || (leaveType === 'Sick Leave' && calculatedDuration >= 3)) && (
                        <div>
                            <label htmlFor="attachment" className="block text-sm font-medium text-gray-700">
                                Attachment ({leaveType === 'Sick Leave' ? "Medical certificate required" : "Supporting document required"})
                            </label>
                            <input type="file" id="attachment" onChange={e => setAttachment(e.target.files[0])} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"/>
                        </div>
                    )}
                    
                    <div className="flex justify-end pt-4">
                        <button type="submit" disabled={isSubmitting || isLoading || !manager} className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700 disabled:bg-blue-400">
                            {isSubmitting ? 'Submitting...' : 'Submit Request'}
                        </button>
                    </div>
                </form>
            </div>
        </>
    );
}